import React, { useEffect, useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	Button,
	Alert,
	Typography,
} from '@mui/material';
import { apiErrorMessage } from '../../../api/catalog';
import { FormError, ModalActions } from './styles';

interface CascadeInfo {
	academias?: number;
	materias?: number;
}

interface ConfirmarEliminarElementoModalProps {
	open: boolean;
	tipo: 'departamento' | 'academia' | 'materia';
	nombre: string;
	cascadeInfo?: CascadeInfo;
	onConfirm: () => void | Promise<void>;
	onCancel: () => void;
}

const TIPO_LABEL: Record<string, string> = {
	departamento: 'departamento',
	academia: 'academia',
	materia: 'materia',
};

const buildCascadeMessage = (tipo: string, info?: CascadeInfo): string | null => {
	if (!info) return null;
	if (tipo === 'departamento') {
		const parts: string[] = [];
		if (info.academias) parts.push(`${info.academias} ${info.academias === 1 ? 'academia' : 'academias'}`);
		if (info.materias) parts.push(`${info.materias} ${info.materias === 1 ? 'materia' : 'materias'}`);
		return parts.length > 0 ? `Esta acción también eliminará ${parts.join(' y ')}.` : null;
	}
	if (tipo === 'academia' && info.materias) {
		return `Esta acción también eliminará ${info.materias} ${info.materias === 1 ? 'materia' : 'materias'}.`;
	}
	return null;
};

const ConfirmarEliminarElementoModal: React.FC<ConfirmarEliminarElementoModalProps> = ({
	open,
	tipo,
	nombre,
	cascadeInfo,
	onConfirm,
	onCancel,
}) => {
	const cascadeMessage = buildCascadeMessage(tipo, cascadeInfo);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (open) {
			setError(null);
			setSaving(false);
		}
	}, [open]);

	const handleConfirm = async () => {
		if (saving) return;
		setSaving(true);
		setError(null);
		try {
			await onConfirm();
		} catch (err) {
			setError(apiErrorMessage(err));
			setSaving(false);
		}
	};

	const handleCancel = () => {
		if (saving) return;
		onCancel();
	};

	return (
		<Dialog open={open} onClose={handleCancel} maxWidth='xs' fullWidth>
			<DialogTitle>Eliminar {TIPO_LABEL[tipo]}</DialogTitle>
			<DialogContent>
				<DialogContentText>
					¿Estás seguro de que deseas eliminar el {TIPO_LABEL[tipo]}{' '}
					<strong>{nombre}</strong>?
				</DialogContentText>
				{cascadeMessage && (
					<Alert severity='warning' variant='outlined' style={{ marginTop: 16 }}>
						<Typography variant='body2'>{cascadeMessage}</Typography>
					</Alert>
				)}
				{error && <FormError severity='error'>{error}</FormError>}
			</DialogContent>
			<ModalActions>
				<Button variant='outlined' onClick={handleCancel} disabled={saving}>
					Cancelar
				</Button>
				<Button variant='contained' color='error' onClick={handleConfirm} disabled={saving}>
					{saving ? 'Eliminando...' : 'Eliminar'}
				</Button>
			</ModalActions>
		</Dialog>
	);
};

export default ConfirmarEliminarElementoModal;
